import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import apiFactory from "../../api";
import { useInfoUser } from "../../store/UserStore";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../utils/Utils";
import { GeneralModal } from "./GeneralModal";

const CalendarModal = ({
  title,
  data,
  permissionCurrentUser,
  onCancel,
  open,
  onDeleteItem,
  onSubmit,
}) => {
  const { projectCode } = useParams();
  const { user, languageMap } = useInfoUser();
  const [form] = Form.useForm();
  const [isFullTime, setIsFullTime] = useState(false);
  // const [isProjectSchedule, setIsProjectSchedule] = useState(
  //   data?.isProjectSchedule
  // );
  const [projectScope, setProjectScope] = useState(data?.projectScope);
  const [isOpenModalConfirm, setIsOpenModalConfirm] = useState(false);
  const [countCheckIsFullTime, setCountCheckIsFullTime] = useState(0);

  const onFinish = async (values) => {
    try {
      values = {
        ...values,
        projectId: projectCode,
        calendarId: data?.calendarId || null,
      };

      let isSuccess = false;
      if (values?.calendarId) {
        const result = await apiFactory.calendarApi.update(values);

        if (result?.status === 200) {
          isSuccess = true;
          openNotificationSuccess(
            languageMap?.["toast.calendar.updateSuccess"] ??
              "Update event data successfully"
          );
        } else if (result?.status === 403) {
          openNotificationError(result?.message);
        } else {
          openNotificationError(
            languageMap?.["toast.calendar.updateFailed"] ??
              "Failed to update event data."
          );
        }
      } else {
        const result = await apiFactory.calendarApi.create(values);

        if (result?.status === 200) {
          isSuccess = true;
          openNotificationSuccess(
            languageMap?.["toast.calendar.createSuccess"] ??
              "Create event data successfully"
          );
        } else {
          openNotificationError(
            languageMap?.["toast.calendar.createFailed"] ??
              "Failed to create event data."
          );
        }
      }

      onSubmit(isSuccess);
    } catch (error) {
      openNotificationError(
        languageMap?.["toast.calendar.fetchFailed"] ??
          "Failed to fetch event data."
      );
    }
  };

  const handleConfirm = () => {
    onDeleteItem(data?.calendarId);
  };

  const checkIsFullTime = () => {
    const startTime = dayjs(data?.startTime);
    const endTime = dayjs(data?.endTime);

    const startTimeHours = startTime.hour();
    const startTimeMinutes = startTime.minute();

    const endTimeHours = endTime.hour();
    const endTimeMinutes = endTime.minute();

    return (
      startTimeHours === 0 &&
      startTimeMinutes === 0 &&
      endTimeHours === 23 &&
      endTimeMinutes === 59
    );
  };

  useEffect(() => {
    if (countCheckIsFullTime === 0) {
      setIsFullTime(checkIsFullTime());
      setCountCheckIsFullTime(countCheckIsFullTime + 1);
    }

    if (open) {
      form.setFieldsValue({
        startTime: form.getFieldValue("startTime")
          ? isFullTime
            ? dayjs(form.getFieldValue("startTime")).startOf("day")
            : dayjs(form.getFieldValue("startTime"))
          : data?.startTime
            ? isFullTime
              ? dayjs(data?.startTime).startOf("day")
              : dayjs(data?.startTime)
            : isFullTime
              ? dayjs(data?.selectDate).startOf("day")
              : dayjs(data?.selectDate),
        endTime: form.getFieldValue("endTime")
          ? isFullTime
            ? dayjs(form.getFieldValue("endTime")).endOf("day")
            : dayjs(form.getFieldValue("endTime"))
          : data?.endTime
            ? isFullTime
              ? dayjs(data?.endTime).endOf("day")
              : dayjs(data?.endTime)
            : isFullTime
              ? dayjs(data?.selectDate).endOf("day")
              : dayjs(data?.selectDate),
      });
    }
  }, [data, form, open, isFullTime]);

  return (
    <>
      <Modal
        width={700}
        title={title}
        open={open}
        onCancel={onCancel}
        footer={null}
        closeIcon={null}
        maskClosable={false}
        centered
      >
        <Form
          form={form}
          name="calendarForm"
          onFinish={onFinish}
          initialValues={{
            calendarId: data?.calendarId,
            projectId: projectCode,
            projectScope: data?.projectScope || false,
            startTime: dayjs(data?.startTime || data?.selectDate),
            endTime: dayjs(data?.endTime || data?.selectDate),
            // isProjectSchedule: data?.isProjectSchedule || false,
            type: data?.type || "WORK",
            content: data?.content,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col span={3}>
              <span>
                {languageMap?.[
                  "event.dashboard.myProject.calendar.startTime"
                ] ?? "Start Time:"}
              </span>
            </Col>
            <Col span={9}>
              <Form.Item
                name="startTime"
                rules={[
                  {
                    required: true,
                    message: "Please select start date",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || !getFieldValue("endTime")) {
                        return Promise.resolve();
                      }
                      if (!value && getFieldValue("endTime")) {
                        return Promise.reject(
                          new Error("Please select start date")
                        );
                      }
                      if (dayjs(value).isAfter(getFieldValue("endTime"))) {
                        return Promise.reject(
                          new Error("Start date must be before end date")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  disabled={
                    data?.calendarId && user?.userCode !== data?.createdBy
                  }
                  style={{ width: "100%" }}
                  showTime={{ format: "HH:mm" }}
                  format={isFullTime ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm"}
                  size="default"
                  placeholder={"Select start date"}
                />
              </Form.Item>
            </Col>

            <Col span={3}>
              {languageMap?.["event.dashboard.myProject.calendar.endTime"] ??
                "End Time:"}
            </Col>
            <Col span={9}>
              <Form.Item
                name="endTime"
                rules={[
                  {
                    required: true,
                    message: "Please select end date",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || !getFieldValue("startTime")) {
                        return Promise.resolve();
                      }
                      if (!value && getFieldValue("startTime")) {
                        return Promise.reject(
                          new Error("Please select end date")
                        );
                      }
                      if (dayjs(value).isBefore(getFieldValue("startTime"))) {
                        return Promise.reject(
                          new Error("End date must be after start date")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  disabled={
                    data?.calendarId && user?.userCode !== data?.createdBy
                  }
                  style={{ width: "100%" }}
                  showTime={{ format: "HH:mm" }}
                  format={isFullTime ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm"}
                  size="default"
                  placeholder={"Select end date"}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="isFullTime">
                <Checkbox
                  disabled={
                    data?.calendarId && user?.userCode !== data?.createdBy
                  }
                  name="isFullTime"
                  onChange={() => setIsFullTime(!isFullTime)}
                  checked={isFullTime}
                />
                <span className="pl-2">
                  {languageMap?.[
                    "event.dashboard.myProject.calendar.fullTime"
                  ] ?? "Full time"}
                </span>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="projectScope">
                <Checkbox
                  disabled={
                    data?.calendarId && user?.userCode !== data?.createdBy
                  }
                  onChange={() => {
                    setProjectScope(!projectScope);
                    form.setFieldValue("projectScope", !projectScope);
                  }}
                  checked={projectScope}
                />
                <span className="pl-2">
                  {languageMap?.[
                    "event.dashboard.myProject.calendar.moda.applyAll"
                  ] ?? "Apply to all projects"}
                </span>
              </Form.Item>
            </Col>

            <Col span={4}>
              {languageMap?.[
                "event.dashboard.myProject.calendar.scheduleType"
              ] ?? "Schedule Type:"}
            </Col>
            <Col span={20}>
              <Form.Item name="type">
                <Select
                  disabled={
                    data?.calendarId && user?.userCode !== data?.createdBy
                  }
                  defaultValue={data?.type}
                  style={{
                    width: "50%",
                  }}
                  options={[
                    {
                      value: "WORK",
                      label:
                        languageMap?.[
                          "event.dashboard.myProject.calendar.scheduleType.work"
                        ] ?? "Work",
                    },
                    {
                      value: "BUSINESS_TRIP",
                      label:
                        languageMap?.[
                          "event.dashboard.myProject.calendar.scheduleType.businessTrip"
                        ] ?? "Business trip",
                    },
                    {
                      value: "VACATION",
                      label:
                        languageMap?.[
                          "event.dashboard.myProject.calendar.scheduleType.vacation"
                        ] ?? "Vacation",
                    },
                    {
                      value: "ETC",
                      label:
                        languageMap?.[
                          "event.dashboard.myProject.calendar.scheduleType.etc"
                        ] ?? "Etc",
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              {languageMap?.["event.dashboard.myProject.calendar.content"] ??
                "Content"}
            </Col>
            <Col span={20}>
              <Form.Item
                name="content"
                rules={[
                  {
                    required: true,
                    message:
                      languageMap?.["toast.calendar.message"] ??
                      "Please input Content!",
                    whitespace: true,
                  },
                  () => ({
                    validator(_, value) {
                      if (value && value.trim().length === 0) {
                        return Promise.reject(
                          new Error("Content cannot be empty")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input.TextArea
                  disabled={
                    data?.calendarId && user?.userCode !== data?.createdBy
                  }
                  rows={4}
                  placeholder={
                    languageMap?.[
                      "event.dashboard.myProject.calendar.content"
                    ] ?? "Content"
                  }
                  value={data?.content}
                  maxLength={250}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <div className="flex justify-end" key={"footer-calendar-model"}>
                <div>
                  {permissionCurrentUser?.includes("C") && !data?.calendarId ? (
                    <Button type="primary" htmlType="submit">
                      <span>
                        {languageMap?.[
                          "event.dashboard.myProject.calendar.moda.writeBtn"
                        ] ?? "Write"}
                      </span>
                    </Button>
                  ) : (
                    data?.calendarId &&
                    user?.userCode === data?.createdBy && (
                      <Button type="primary" htmlType="submit">
                        <span>
                          {languageMap?.[
                            "event.dashboard.myProject.calendar.moda.updateBtn"
                          ] ?? "Update"}
                        </span>
                      </Button>
                    )
                  )}
                </div>
                <div>
                  {data?.calendarId && user?.userCode === data?.createdBy && (
                    <Button
                      className="ml-1"
                      type="primary"
                      htmlType="submit"
                      danger
                      onClick={() => setIsOpenModalConfirm(true)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
                <div>
                  <Button htmlType="submit" className="ml-1" onClick={onCancel}>
                    {languageMap?.[
                      "event.dashboard.myProject.calendar.moda.closeBtn"
                    ] ?? " Close"}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
      <GeneralModal
        title={"Delete event"}
        content={"You want to delete event?"}
        onCancel={() => setIsOpenModalConfirm(false)}
        open={isOpenModalConfirm}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export { CalendarModal };
